import React, {useState} from 'react';
import {router} from '@inertiajs/react';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    InputGroup,
    ListGroup,
    Row,
} from 'react-bootstrap';
import PermissionMatrix, {PermissionSaveBar, usePermissionDraft} from '../Components/PermissionMatrix.jsx';
import HelpTooltip from '../Components/HelpTooltip.jsx';
import ConfirmDialog from '../Components/OrderEditor/ConfirmDialog.jsx';

const roleLabel = name => (name === 'superadmin' ? 'Superadmin' : name);

const READ_ONLY_HINT = 'Rollen ändert nur der Superadmin. Du kannst die Rechte jeder Rolle ansehen '
    + 'und Rollen den Benutzern zuweisen oder entziehen (Seite „Benutzer“).';

export default function Roles({
    roles = [],
    permissions = [],
    areas = [],
    can_manage_roles: canManage = false,
}) {
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleError, setNewRoleError] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState(roles[0]?.id);
    const [pendingRoleId, setPendingRoleId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const selectedRole = roles.find(role => role.id === selectedRoleId) || roles[0];

    const savedNames = (selectedRole?.permissions || []).map(permission => permission.name);
    const {draft, setDraft, count, reset} = usePermissionDraft(savedNames);

    const createRole = event => {
        event.preventDefault();
        if (!newRoleName.trim()) {
            setNewRoleError('Bitte einen Namen für die neue Rolle eingeben.');
            return;
        }
        setNewRoleError('');
        router.post('/roles', {name: newRoleName.trim()}, {
            preserveScroll: true,
            onSuccess: () => setNewRoleName(''),
            onError: errors => setNewRoleError(errors.name || 'Die Rolle konnte nicht angelegt werden.'),
        });
    };

    const saveDraft = () => {
        const draftSet = new Set(draft);
        setSaving(true);
        setSaveError('');
        router.post('/roles/' + selectedRole.id + '/permissions', {
            permissions: permissions.filter(permission => draftSet.has(permission.name)).map(permission => permission.id),
        }, {
            preserveScroll: true,
            preserveState: true,
            onError: errors => setSaveError(Object.values(errors)[0] || 'Änderung konnte nicht gespeichert werden.'),
            onFinish: () => setSaving(false),
        });
    };

    const selectRole = roleId => {
        if (roleId === selectedRole?.id) return;
        if (count > 0) {
            setPendingRoleId(roleId);
            return;
        }
        setSaveError('');
        setSelectedRoleId(roleId);
    };

    const deleteSelectedRole = () => {
        setConfirmDelete(false);
        router.delete('/roles/' + selectedRole.id, {preserveScroll: true});
    };

    if (! selectedRole) {
        return (
            <Container fluid className="py-3">
                <h1>Rollenverwaltung</h1>
                <p className="text-muted">Noch keine Rollen vorhanden.</p>
            </Container>
        );
    }

    const selectedIsLocked = selectedRole.name === 'superadmin';

    return (
        <Container fluid className="py-3">
            <div className="mb-3">
                <h1 className="mb-0">Rollenverwaltung</h1>
                <small className="text-muted">
                    Rolle auswählen und Rechte übersichtlich nach Funktionsbereich vergeben.
                </small>
            </div>

            {canManage && (
                <Form noValidate onSubmit={createRole} className="mb-4 d-flex align-items-center gap-2 flex-wrap">
                    <InputGroup style={{maxWidth: 420}}>
                        <Form.Control
                            aria-label="Name der neuen Rolle"
                            aria-invalid={newRoleError ? 'true' : undefined}
                            isInvalid={!!newRoleError}
                            value={newRoleName}
                            onChange={event => { setNewRoleName(event.target.value); setNewRoleError(''); }}
                            placeholder="Neue Rolle"
                        />
                        <Button type="submit">Rolle anlegen</Button>
                    </InputGroup>
                    <HelpTooltip id="rollen-neu-hilfe" ariaLabel="Hilfe zum Anlegen einer Rolle" placement="right">
                        Legt eine leere Rolle an. Ihre Rechte stellst du danach in der Matrix rechts ein.
                        Rollen anlegen, löschen und ihre Rechte ändern darf nur der Superadmin.
                    </HelpTooltip>
                    {newRoleError && <div className="text-danger small w-100" role="alert">{newRoleError}</div>}
                </Form>
            )}

            <Row className="g-3 align-items-start">
                <Col lg={4} xl={3}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white fw-semibold d-flex align-items-center gap-2">
                            Rollen
                            <HelpTooltip id="rollen-liste-hilfe" ariaLabel="Hilfe zur Rollenliste" placement="right">
                                Die Zahl rechts nennt, wie viele Rechte die Rolle trägt. Geschützte Rechte
                                (z. B. Verkaufsstatistik, Cosmos) gehören nie zu einer Rolle, sie werden
                                einzeln am Benutzer vergeben.
                            </HelpTooltip>
                        </Card.Header>
                        <ListGroup variant="flush">
                            {roles.map(role => {
                                const locked = role.name === 'superadmin';
                                return (
                                    <ListGroup.Item
                                        key={role.id}
                                        action
                                        active={role.id === selectedRole.id}
                                        onClick={() => selectRole(role.id)}
                                        className="d-flex justify-content-between align-items-center"
                                        data-role-id={role.id}
                                    >
                                        <span>
                                            {roleLabel(role.name)}
                                            {locked && <span aria-label="geschützt" className="ms-1">🔒</span>}
                                        </span>
                                        <Badge
                                            bg={role.id === selectedRole.id ? 'light' : 'secondary'}
                                            text={role.id === selectedRole.id ? 'dark' : undefined}
                                            pill
                                        >
                                            {locked ? 'alle' : role.permissions.length}
                                        </Badge>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </Card>
                </Col>

                <Col lg={8} xl={9}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white d-flex flex-wrap gap-2 justify-content-between align-items-center">
                            <div className="fw-semibold">
                                {roleLabel(selectedRole.name)}
                                {selectedIsLocked && (
                                    <Badge bg="dark" pill className="ms-2">
                                        Vollzugriff · gesperrt
                                    </Badge>
                                )}
                            </div>
                            {! selectedIsLocked && (
                                <Badge bg="light" text="dark">
                                    {selectedRole.permissions.length} Rechte
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {selectedIsLocked ? (
                                <p className="mb-0 text-muted">
                                    Superadmin erhält automatisch alle bestehenden und neuen
                                    Berechtigungen. Diese Rolle kann nicht verändert oder gelöscht
                                    werden.
                                </p>
                            ) : (
                                <>
                                    <PermissionMatrix
                                        key={selectedRole.id}
                                        idPrefix={`rolle-${selectedRole.id}`}
                                        permissions={permissions}
                                        areas={areas}
                                        selected={draft}
                                        onChange={setDraft}
                                        readOnly={! canManage || saving}
                                        readOnlyHint={! canManage ? READ_ONLY_HINT : null}
                                    />

                                    {canManage && (
                                        <PermissionSaveBar
                                            count={count}
                                            saving={saving}
                                            error={saveError}
                                            onSave={saveDraft}
                                            onDiscard={() => {
                                                reset();
                                                setSaveError('');
                                            }}
                                        />
                                    )}

                                    {canManage && (
                                        <div className="mt-3 d-flex align-items-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="roles-delete"
                                                onClick={() => setConfirmDelete(true)}
                                            >
                                                Rolle löschen
                                            </Button>
                                            <HelpTooltip id="rolle-loeschen-hilfe" ariaLabel="Hilfe zum Löschen" placement="right">
                                                Löscht die Rolle samt ihrer Rechte-Zuordnung. Benutzer, die
                                                sie tragen, verlieren die damit verbundenen Rechte.
                                            </HelpTooltip>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <ConfirmDialog
                show={confirmDelete}
                title="Rolle löschen?"
                confirmLabel="Löschen"
                tone="danger"
                onConfirm={deleteSelectedRole}
                onCancel={() => setConfirmDelete(false)}
            >
                Rolle „{roleLabel(selectedRole.name)}“ wirklich löschen? Benutzer mit dieser Rolle
                verlieren die damit verbundenen Rechte.
            </ConfirmDialog>

            <ConfirmDialog
                show={pendingRoleId !== null}
                title="Änderungen verwerfen?"
                confirmLabel="Verwerfen und wechseln"
                tone="danger"
                onConfirm={() => {
                    reset();
                    setSaveError('');
                    setSelectedRoleId(pendingRoleId);
                    setPendingRoleId(null);
                }}
                onCancel={() => setPendingRoleId(null)}
            >
                Für die Rolle „{roleLabel(selectedRole.name)}“ gibt es ungespeicherte Änderungen. Beim
                Wechsel gehen sie verloren.
            </ConfirmDialog>
        </Container>
    );
}
